import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { B } from "../data/pillars";
import SectionHeader from "./SectionHeader";

const SOURCES = {
  itic: {
    label: "ITIC 2024",
    tag: "RECOMMENDED",
    tagColor: B.green,
    rateFloor: 8000,
    rateCeiling: 25000,
    midRate: 9000,
    perHr: "$8,000–$25,000/hr",
    perMin: "$133–$417/min",
    desc: "ITIC's 2024 Hourly Cost of Downtime Survey (n=1,000+ firms, 11th consecutive year). SMBs with 20–200 employees report $8,000–$25,000/hr. Same source cited by IBM, Microsoft, and ConnectWise. Strongest peer-reviewed SMB dataset available.",
    useWhen: "Executive overview / CFO / Risk Committee",
    citation: "ITIC 2024 Hourly Cost of Downtime Survey",
  },
  corptech: {
    label: "Corporate Technologies 2026",
    tag: "SMB-SPECIFIC",
    tagColor: B.teal,
    rateFloor: 8000,
    rateCeiling: 10000,
    midRate: 9000,
    perHr: "Up to $10,000/hr",
    perMin: "$133–$167/min",
    desc: "Corporate Technologies 2026 SMB Downtime Whitepaper: up to $10,000/hr for SMBs during critical outages. Primary research, SMB-specific, published January 2026. Tighter range — ideal for owner/ops leader conversations.",
    useWhen: "Owner / Operations Leader",
    citation: "Corporate Technologies SMB Downtime Whitepaper, Jan 2026",
  },
  gartner: {
    label: "Gartner 2014",
    tag: "BASELINE",
    tagColor: B.yellow,
    rateFloor: 5600,
    rateCeiling: 5600,
    midRate: 5600,
    perHr: "$5,600/hr",
    perMin: "$93/min",
    desc: "Widely cited 2014 blended SMB average. Historical baseline only — current costs are significantly higher due to cloud dependency, remote workforce, and SaaS sprawl. Use only for conservative historical comparison.",
    useWhen: "Historical baseline comparison",
    citation: "Gartner IT Downtime Cost Study, 2014",
  },
};

// Three-point model per OML band
// Each band has low/mid/high hours AND low/mid/high costs computed at presentation
const OML_BANDS = [
  {
    maxScore: 2,
    low: 120, mid: 160, high: 200,
    uptimePct: "95–98%",
    incidents: "6–10x / year",
    hrsPerIncident: "12–24 hrs",
    basis: "No monitoring, reactive-only IT (ConnectWise OML 1–2). Incidents go undetected until users report them. ITIC 2024 confirms these environments average 95–98% uptime.",
    verifyPrompt: "How many times last year did you experience an outage, and how long did it take to resolve?",
    sourceNote: "Per IT Solutions 2026: break-fix models deliver 50–85% more downtime than monitored managed environments. 120–240 hrs is a conservative subset of this range.",
  },
  {
    maxScore: 3,
    low: 44, mid: 88, high: 131,
    uptimePct: "98.5–99.5%",
    incidents: "3–6x / year",
    hrsPerIncident: "8–16 hrs",
    basis: "Some monitoring, inconsistent patching. Partial visibility extends detection and recovery time. ITIC 2024: these environments approach 99% uptime but lack full RMM coverage.",
    verifyPrompt: "Were recent IT issues caught proactively by systems, or reported by end users?",
    sourceNote: "44–131 hrs maps to 98.5–99.5% uptime per ITIC 2024 SMB cohort data.",
  },
  {
    maxScore: 4,
    low: 16, mid: 30, high: 44,
    uptimePct: "99.5–99.9%",
    incidents: "1–3x / year",
    hrsPerIncident: "4–8 hrs",
    basis: "Managed tools with gaps in automation and DR testing. Issues caught faster but recovery still partially manual.",
    verifyPrompt: "Is there a documented disaster recovery plan, and when was it last successfully tested?",
    sourceNote: "16–44 hrs maps to 99.5–99.9% uptime. ConnectWise Service Leadership: OML 3 organizations show significantly improved recovery metrics vs. reactive environments.",
  },
  {
    maxScore: 5,
    low: 4, mid: 10, high: 16,
    uptimePct: "99.9%+",
    incidents: "< 1x / year",
    hrsPerIncident: "2–4 hrs",
    basis: "24/7 RMM, proactive patching, tested DR plan. Issues are caught and resolved before users notice.",
    verifyPrompt: "Are NOC escalation logs and DR test results consistently documented and reviewed?",
    sourceNote: "4–16 hrs maps to 99.9%+ uptime. Represents fully managed proactive IT per ITIC 2024 top-quartile SMB cohort.",
  },
];

function getDowntimeBand(avgScore) {
  return OML_BANDS.find(b => avgScore < b.maxScore) || OML_BANDS[OML_BANDS.length - 1];
}

const TARGET_BAND = OML_BANDS.find(b => b.maxScore === 4);

const BONUS_VALIDATORS = [
  {
    icon: "🔧",
    label: "Emergency Response Costs",
    desc: "Reactive IT environments typically spend 20–30% more annually on unplanned emergency work compared to proactive managed models. (IT Solutions, 2026)",
    color: B.orange,
  },
  {
    icon: "💾",
    label: "Disaster Recovery Cost Differential",
    desc: "Proactive disaster recovery planning costs significantly less than emergency response and data reconstruction after an incident. Tested DR plans reduce recovery time by 60–80%.",
    color: B.teal,
  },
  {
    icon: "👥",
    label: "Workforce Productivity Impact",
    desc: "IDC research indicates IT downtime can cause up to 20% productivity loss — representing hundreds of lost work hours annually across your team.",
    color: "#9B59B6",
  },
];

function fmt(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function UptimeBadge({ pct }) {
  return (
    <span style={{
      background: B.blue + "20", border: `1px solid ${B.blue}40`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11,
      color: B.accent, fontWeight: 700, fontFamily: "monospace"
    }}>
      {pct} uptime
    </span>
  );
}

function ThreePointRow({ label, low, mid, high, color, unit = "" }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: "#0D1B2E", borderRadius: 8, padding: "10px 14px",
      border: `1px solid ${color}20`, flexWrap: "wrap"
    }}>
      <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", flex: "1 1 100px", minWidth: 80 }}>
        {label}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: B.subText, marginBottom: 2 }}>LOW</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: B.lightGray, fontFamily: "monospace" }}>{unit}{low}</div>
        </div>
        <div style={{ color: B.subText, fontSize: 12, padding: "0 4px" }}>·</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: color, marginBottom: 2, fontWeight: 700 }}>MID ★</div>
          <div style={{ fontSize: 16, fontWeight: 900, color, fontFamily: "monospace" }}>{unit}{mid}</div>
        </div>
        <div style={{ color: B.subText, fontSize: 12, padding: "0 4px" }}>·</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: B.subText, marginBottom: 2 }}>HIGH</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: B.lightGray, fontFamily: "monospace" }}>{unit}{high}</div>
        </div>
      </div>
    </div>
  );
}

function SourceSelector({ active, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Object.entries(SOURCES).map(([key, src]) => {
        const isActive = active === key;
        return (
          <button key={key} onClick={() => onChange(key)} style={{
            background: isActive ? B.blue + "18" : "#0D1B2E",
            border: `1.5px solid ${isActive ? B.blue : "#1E3A5F"}`,
            borderRadius: 10, padding: "12px 16px", cursor: "pointer",
            textAlign: "left", transition: "all 0.15s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ color: isActive ? B.white : B.lightGray, fontWeight: 800, fontSize: 13 }}>{src.label}</span>
              <span style={{
                background: src.tagColor + "25", color: src.tagColor,
                fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 3, letterSpacing: 0.5
              }}>{src.tag}</span>
              <span style={{ marginLeft: "auto", color: B.subText, fontSize: 11, fontStyle: "italic" }}>Best for: {src.useWhen}</span>
            </div>
            <div style={{ fontSize: 11, color: isActive ? B.lightGray : B.subText, lineHeight: 1.6 }}>{src.desc}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: isActive ? B.accent : "#3A5A7A", fontWeight: 700, fontFamily: "monospace" }}>
              {src.perHr} · {src.perMin}
            </div>
            {isActive && (
              <div style={{ marginTop: 6, fontSize: 10, color: B.subText, fontStyle: "italic" }}>
                📎 {src.citation}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function ROICalculator({ employees, avgScore }) {
  const [activeSource, setActiveSource] = useState("itic");
  const [showMethodology, setShowMethodology] = useState(false);
  const [showBenchmarks, setShowBenchmarks] = useState(false);
  const [showOmlMapping, setShowOmlMapping] = useState(false);

  const src = SOURCES[activeSource];
  const band = getDowntimeBand(avgScore);
  const riskColor = avgScore < 2 ? B.red : avgScore < 3 ? B.orange : avgScore < 4 ? B.yellow : B.green;

  // Three-point cost model
  const costLow = band.low * src.rateFloor;
  const costMid = band.mid * src.midRate;
  const costHigh = band.high * src.rateCeiling;

  // Savings vs OML 4 target (mid-to-mid)
  const savingsLow = (band.low - TARGET_BAND.low) * src.rateFloor;
  const savingsMid = (band.mid - TARGET_BAND.mid) * src.midRate;
  const showSavings = avgScore < 4 && savingsLow > 0;

  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
      padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box"
    }}>
      <SectionHeader num={6} title="Downtime Exposure Estimate" sub="Research-Backed Three-Point Cost Model" />

      {/* OML → UPTIME BRIDGE */}
      <div style={{
        background: "#122240", borderRadius: 10, padding: "14px 18px", marginBottom: 20,
        borderLeft: `4px solid ${B.teal}`
      }}>
        <div style={{ fontSize: 11, color: B.teal, fontWeight: 700, letterSpacing: 2, marginBottom: 8 }}>
          HOW OML MAPS TO UPTIME
        </div>
        <p style={{ color: B.lightGray, fontSize: 13, lineHeight: 1.7, margin: "0 0 8px" }}>
          Reactive IT environments (ConnectWise OML 1–2) lack proactive monitoring and patching controls.
          Per IT Solutions' 2026 analysis, break-fix models deliver <strong style={{ color: B.white }}>50–85% more downtime</strong> than
          monitored managed service environments. ITIC's 2024 survey confirms these environments average{" "}
          <strong style={{ color: B.white }}>95–98% uptime</strong> versus{" "}
          <strong style={{ color: B.green }}>99.5–99.9%</strong> for fully managed models — a gap of{" "}
          <strong style={{ color: riskColor }}>175–438 unplanned hours per year</strong>.
        </p>
        <p style={{ color: B.subText, fontSize: 12, lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>
          OML {avgScore.toFixed(1)} estimate of {band.low}–{band.high} hrs represents a conservative subset of this range.
          Client incident logs can validate or adjust this figure.
        </p>
        <button onClick={() => setShowOmlMapping(v => !v)} style={{
          marginTop: 10, background: "none", border: "none", color: B.teal, fontSize: 12,
          cursor: "pointer", fontWeight: 600, padding: 0
        }}>
          {showOmlMapping ? "▲ Hide" : "▼ View"} full OML-to-uptime mapping table
        </button>
        <AnimatePresence>
          {showOmlMapping && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #1E3A5F" }}>
                    {["OML Band", "Uptime %", "Annual Hrs Down", "Incident Freq", "Source Basis"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 10px", color: B.lightGray, fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {OML_BANDS.map((b, i) => {
                    const colors = [B.red, B.orange, B.yellow, B.green];
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #1E3A5F20" }}>
                        <td style={{ padding: "8px 10px", color: colors[i], fontWeight: 700 }}>OML {i + 1}–{b.maxScore}</td>
                        <td style={{ padding: "8px 10px", color: B.white, fontFamily: "monospace" }}>{b.uptimePct}</td>
                        <td style={{ padding: "8px 10px", color: B.lightGray }}>{b.low}–{b.high} hrs/yr</td>
                        <td style={{ padding: "8px 10px", color: B.subText }}>{b.incidents}</td>
                        <td style={{ padding: "8px 10px", color: B.subText, fontSize: 10, fontStyle: "italic" }}>ITIC 2024 / IT Solutions 2026</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: 10, color: B.subText, fontStyle: "italic", lineHeight: 1.6 }}>
                Sources: ITIC 2024 Hourly Cost of Downtime Survey · IT Solutions 2026 Proactive IT Whitepaper · ConnectWise Service Leadership OML Framework
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* BENCHMARK SELECTOR */}
      <div style={{ background: "#122240", borderRadius: 12, padding: "18px 20px", marginBottom: 20, border: "1px solid #1E3A5F" }}>
        <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 2, marginBottom: 14 }}>
          STEP 1 — SELECT COST BENCHMARK
        </div>
        <SourceSelector active={activeSource} onChange={setActiveSource} />
      </div>

      {/* DOWNTIME HOURS + THREE-POINT MODEL */}
      <div style={{
        background: "#122240", borderRadius: 14,
        border: `2px solid ${riskColor}40`, padding: "22px 26px", marginBottom: 16
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
            STEP 2 — DOWNTIME HOURS · OML {avgScore.toFixed(1)}
          </div>
          <UptimeBadge pct={band.uptimePct} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          <ThreePointRow
            label="Annual Downtime"
            low={`${band.low} hrs`} mid={`${band.mid} hrs`} high={`${band.high} hrs`}
            color={riskColor}
          />
          <ThreePointRow
            label="Cost Exposure"
            low={fmt(costLow)} mid={fmt(costMid)} high={fmt(costHigh)}
            color={riskColor}
          />
        </div>

        <div style={{
          background: "#0D1B2E", borderRadius: 8, padding: "10px 14px",
          fontSize: 11, color: B.subText, lineHeight: 1.7, marginBottom: 14,
          borderLeft: `3px solid ${B.blue}`
        }}>
          <strong style={{ color: B.lightGray }}>Three-point model:</strong>{" "}
          Low = {band.low} hrs × ${src.rateFloor.toLocaleString()}/hr ·{" "}
          Mid (Most Likely) = {band.mid} hrs × ${src.midRate.toLocaleString()}/hr ·{" "}
          High = {band.high} hrs × ${src.rateCeiling.toLocaleString()}/hr.
          Present Mid and Low to leadership — the 1.5:1 ratio is defensible. The CFO challenge shifts from
          "why is the range so wide?" to "walk me through how you got to {band.mid} hours" — which incident logs can answer.
        </div>

        {/* HIGHLIGHTED CONSERVATIVE CASE */}
        <div style={{
          background: "#0D1B2E", borderRadius: 12,
          padding: "18px 20px", border: `1px solid ${riskColor}30`
        }}>
          <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>
            STEP 3 — CONSERVATIVE CASE (PRESENT TO CFO)
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 900, color: riskColor, lineHeight: 1 }}>{fmt(costLow)}</span>
            <span style={{ fontSize: 20, color: B.subText, fontWeight: 700 }}>–</span>
            <span style={{ fontSize: 36, fontWeight: 900, color: riskColor, lineHeight: 1 }}>{fmt(costMid)}</span>
            <span style={{ fontSize: 13, color: B.subText }}>per year · conservative–most likely</span>
          </div>
          <div style={{ background: "#1A2E4A", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 12 }}>
            <motion.div
              initial={{ width: 0 }} animate={{ width: "100%" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              style={{ height: "100%", background: `linear-gradient(90deg,${riskColor}60,${riskColor})`, borderRadius: 6 }}
            />
          </div>
          <p style={{ fontSize: 13, color: B.lightGray, lineHeight: 1.8, margin: "0 0 10px" }}>
            "Estimated annual downtime exposure:{" "}
            <strong style={{ color: riskColor }}>{fmt(costLow)}–{fmt(costMid)}</strong> (conservative–most likely case).
            Full methodology available through EdgeCheck Technical Audit."
          </p>
          <div style={{ fontSize: 11, color: B.subText, fontStyle: "italic", lineHeight: 1.6 }}>
            📎 {src.citation}
          </div>
        </div>

        <div style={{ marginTop: 14, background: B.blue + "10", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: B.subText, lineHeight: 1.7, borderLeft: `3px solid ${B.accent}` }}>
          <strong style={{ color: B.accent }}>💡 Validation Question:</strong> {band.verifyPrompt}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: B.subText, fontStyle: "italic", lineHeight: 1.6 }}>
          {band.sourceNote}
        </div>
      </div>

      {/* SAVINGS CARD */}
      {showSavings && (
        <div style={{
          background: B.green + "0D", border: `1px solid ${B.green}30`,
          borderRadius: 12, padding: "18px 22px", marginBottom: 16
        }}>
          <div style={{ fontSize: 11, color: B.green, fontWeight: 800, letterSpacing: 2, marginBottom: 10 }}>
            ↓ POTENTIAL SAVINGS — MOVING TO OML 4.0
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "0 0 auto" }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: B.green }}>{fmt(savingsLow)}–{fmt(savingsMid)}</div>
              <div style={{ fontSize: 11, color: B.subText, marginTop: 4 }}>estimated annual downtime cost reduction</div>
              <div style={{ fontSize: 10, color: B.subText, marginTop: 2, fontStyle: "italic" }}>
                {src.citation} · 50–85% reduction (IT Solutions 2026)
              </div>
            </div>
            <div style={{ flex: "1 1 200px", fontSize: 13, color: B.lightGray, lineHeight: 1.7 }}>
              Moving from OML {avgScore.toFixed(1)} to OML 4.0 reduces estimated downtime from{" "}
              <strong style={{ color: B.white }}>{band.low}–{band.mid} hrs</strong> to{" "}
              <strong style={{ color: B.green }}>{TARGET_BAND.low}–{TARGET_BAND.mid} hrs/year</strong>.
              IT Solutions 2026 research indicates proactive managed IT delivers 50–85% fewer downtime hours
              compared to reactive break-fix models.
            </div>
          </div>
        </div>
      )}

      {/* ADDITIONAL COST FACTORS */}
      <div style={{ background: "#122240", borderRadius: 12, padding: "18px 22px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 2, marginBottom: 14 }}>
          ADDITIONAL COST FACTORS TO CONSIDER
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {BONUS_VALIDATORS.map(v => (
            <div key={v.label} style={{
              display: "flex", gap: 12, alignItems: "flex-start",
              background: "#0D1B2E", borderRadius: 8, padding: "12px 14px",
              border: `1px solid ${v.color}20`
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, background: v.color + "20",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0
              }}>{v.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: v.color, fontWeight: 700, fontSize: 13, marginBottom: 3 }}>{v.label}</div>
                <div style={{ color: B.lightGray, fontSize: 12, lineHeight: 1.6 }}>{v.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: B.subText, lineHeight: 1.7, fontStyle: "italic" }}>
          These factors are not included in the downtime estimate above. Each represents additional financial exposure
          addressable through a formal EdgeCheck Technical Audit.
        </div>
      </div>

      {/* EDGECHECK BRIDGE */}
      <div style={{
        background: "#0D1B2E", borderRadius: 10, padding: "14px 18px",
        fontSize: 12, color: B.subText, lineHeight: 1.8,
        borderLeft: `3px solid ${B.accent}`
      }}>
        These estimates are based on published industry benchmarks scoped to your maturity profile.{" "}
        <strong style={{ color: B.accent }}>Environment-specific modeling</strong> — using your actual incident history,
        recovery times, and business impact data — is available through{" "}
        <strong style={{ color: B.accent }}>VEIT's EdgeCheck Technical Audit</strong>.
        The audit delivers a formal financial model with client-specific incident log validation.
      </div>

      {/* METHODOLOGY TOGGLES */}
      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button onClick={() => setShowMethodology(v => !v)}
          style={{ background: "none", border: "1px solid #1E3A5F", borderRadius: 8, padding: "8px 16px", color: B.subText, fontSize: 12, cursor: "pointer" }}>
          {showMethodology ? "▲ Hide" : "▼ Show"} Methodology Details
        </button>
        <button onClick={() => setShowBenchmarks(v => !v)}
          style={{ background: "none", border: "1px solid #1E3A5F", borderRadius: 8, padding: "8px 16px", color: B.subText, fontSize: 12, cursor: "pointer" }}>
          {showBenchmarks ? "▲ Hide" : "▼ Show"} Benchmark Sources
        </button>
      </div>

      <AnimatePresence>
        {showMethodology && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ marginTop: 14, background: "#0D1B2E", borderRadius: 10, padding: 20, fontSize: 12, color: B.subText, lineHeight: 2 }}>
            <strong style={{ color: B.lightGray }}>Three-Point Downtime Model:</strong><br />
            Low = Annual Downtime (conservative hrs) × Rate Floor<br />
            Mid = Annual Downtime (most likely hrs) × Mid Rate — <em style={{ color: B.accent }}>use this for CFO conversations</em><br />
            High = Annual Downtime (high hrs) × Rate Ceiling<br /><br />
            <strong style={{ color: B.lightGray }}>Downtime hours by OML band (uptime-math backed):</strong><br />
            • Formula: Annual Downtime = (1 − Uptime%) × 8,760 hrs/yr<br />
            • OML 1–2 (reactive): 95–98% uptime → <strong>120 / 160 / 200 hrs/yr</strong> (low/mid/high)<br />
            • OML 2–3 (partial managed): 98.5–99.5% uptime → <strong>44 / 88 / 131 hrs/yr</strong><br />
            • OML 3–4 (managed with gaps): 99.5–99.9% uptime → <strong>16 / 30 / 44 hrs/yr</strong><br />
            • OML 4+ (fully proactive): 99.9%+ uptime → <strong>4 / 10 / 16 hrs/yr</strong><br /><br />
            <strong style={{ color: B.lightGray }}>Why present Low–Mid only?</strong><br />
            Low–Mid produces a ~1.5:1 ratio, which is defensible in a CFO conversation.
            A 2:1 ratio (min × min rate vs. max × max rate) invites dismissal of both endpoints as speculative.
            The Mid figure anchors the conversation to "walk me through how you got to X hours" — which incident logs can answer.<br /><br />
            <strong style={{ color: B.lightGray }}>Savings formula:</strong><br />
            Savings = (Hours_current − Hours_OML4) × Rate. IT Solutions 2026 documents 50–85% downtime reduction for proactive managed IT vs. reactive break-fix.
          </motion.div>
        )}
        {showBenchmarks && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ marginTop: 14, background: "#0D1B2E", borderRadius: 10, padding: 20, fontSize: 12, color: B.subText, lineHeight: 2 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1E3A5F" }}>
                  {["Source", "Rate", "Audience", "Citation Posture"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "6px 10px", color: B.lightGray, fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(SOURCES).map(([key, src]) => (
                  <tr key={key} style={{ borderBottom: "1px solid #1E3A5F20" }}>
                    <td style={{ padding: "8px 10px", color: src.tagColor, fontWeight: 700 }}>{src.label}</td>
                    <td style={{ padding: "8px 10px", color: B.white, fontFamily: "monospace" }}>{src.perHr}</td>
                    <td style={{ padding: "8px 10px", color: B.lightGray }}>{src.useWhen}</td>
                    <td style={{ padding: "8px 10px", color: B.subText, fontStyle: "italic", fontSize: 10 }}>{src.citation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 16, borderTop: "1px solid #1E3A5F", paddingTop: 12 }}>
              <div style={{ color: B.lightGray, fontWeight: 700, marginBottom: 8 }}>Additional Sources Referenced:</div>
              <div style={{ color: B.subText, lineHeight: 2 }}>
                • <strong style={{ color: B.lightGray }}>IT Solutions 2026:</strong> Proactive managed IT reduces downtime 50–85% vs. break-fix environments<br />
                • <strong style={{ color: B.lightGray }}>ConnectWise / Service Leadership:</strong> OML 1–2 defined as reactive-only with sub-median financial performance<br />
                • <strong style={{ color: B.lightGray }}>IDC:</strong> IT downtime causes up to 20% workforce productivity loss<br />
                • <strong style={{ color: B.lightGray }}>Gartner 2014:</strong> $5,600/hr blended SMB baseline (historical reference only)
              </div>
            </div>
            <div style={{ marginTop: 12, color: B.subText, fontStyle: "italic" }}>
              All benchmarks drawn from published third-party research. Validate against client incident history for environment-specific accuracy.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}